import { Popper } from '@material-ui/core'
import { styled, Typography } from 'opensolar-ui'
import { memo, useCallback, useMemo, useState } from 'react'
import useImagePrioritization from './hardwareSelectorV2/useImagePrioritization'

const TooltipContainer = styled('div')(() => ({
  backgroundColor: '#333',
  color: 'white',
  padding: '8px 12px',
  borderRadius: '6px',
  fontSize: '12px',
  boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.25)',
  maxWidth: '200px',
  textAlign: 'center',
}))

const Logo = styled('img')<{ isPlaceholderImage: boolean }>(({ theme, isPlaceholderImage }) => ({
  objectFit: 'contain',
  width: isPlaceholderImage ? 'calc(100% - 8px)' : '100%',
  height: isPlaceholderImage ? 'calc(100% - 8px)' : '100%',
  ...(isPlaceholderImage && { backgroundColor: theme.palette.grey[100], padding: 4 }),
}))

const Title = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '12px !important',
  width: '100%',
  marginBlock: 'auto',
})

type SupplierLogoWithFallbackProps = {
  logoUrl: string | undefined
  name: string
  shouldRenderImage?: boolean
}

const SupplierLogoWithFallback: React.FC<SupplierLogoWithFallbackProps> = ({ logoUrl, name, shouldRenderImage }) => {
  const imageUrls = useMemo(() => [logoUrl, `${window.PUBLIC_URL}/images/component-image-placeholder.png`], [logoUrl])
  const { currentImageUrl, handleImageError, isPlaceholderImage } = useImagePrioritization(imageUrls)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLImageElement | HTMLParagraphElement>) => {
    setAnchorEl(e.currentTarget)
  }, [])
  const handleMouseLeave = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <>
      {shouldRenderImage ?? logoUrl?.length ? (
        <Logo
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          isPlaceholderImage={isPlaceholderImage}
          src={currentImageUrl}
          onError={handleImageError}
          alt={name}
        />
      ) : (
        <Title onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {name}
        </Title>
      )}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        style={{ zIndex: 9999 }}
        placement="bottom"
        modifiers={{
          offset: {
            enabled: true,
            offset: '0,8',
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
          },
        }}
      >
        <TooltipContainer>{name}</TooltipContainer>
      </Popper>
    </>
  )
}

export default memo(SupplierLogoWithFallback)
